import styled from "styled-components";
import { HiLockClosed, HiUsers } from "react-icons/hi2";
import { useTheme } from "styled-components";
import { AccessLevel } from "./types";

const StyledAccessButton = styled.button<{
  tone: "default" | "muted" | "blue";
  isActive: boolean;
}>`
  ${({ tone, isActive, theme: { colors, radii, space, fontSizes, fontWeights } }) => `
    height: 40px;
    padding: 0 ${space[2]} 0 14px;
    box-sizing: border-box;
    border-radius: ${radii[0]};
    display: inline-flex;
    align-items: center;
    gap: ${space[1]};
    white-space: nowrap;
    font-family: "Inter", sans-serif;
    font-size: ${fontSizes[1]};
    font-weight: ${fontWeights.medium};
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
    background: ${tone === "muted" ? colors.background : colors.white};
    color: ${tone === "blue" ? colors.blue.primary : colors.gray[80]};
    border: 1px solid ${
      isActive
        ? colors.blue.primary
        : tone === "blue"
        ? colors.blue[20]
        : tone === "muted"
        ? colors.gray[20]
        : colors.gray[30]
    };
    ${isActive ? `box-shadow: 0 0 0 3px ${colors.blue[50]};` : ""}

    &:hover {
      border-color: ${isActive ? colors.blue.primary : colors.gray[40]};
    }
  `}
`;

const CountPill = styled.span`
  ${({ theme: { colors, fontSizes, fontWeights } }) => `
    min-width: 22px;
    height: 22px;
    padding: 0 7px;
    box-sizing: border-box;
    border-radius: 11px;
    background: ${colors.blue[50]};
    color: ${colors.blue.primary};
    font-size: ${fontSizes[0]};
    font-weight: ${fontWeights.semibold};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  `}
`;

interface Props {
  /** Number of additional Divisions + Teams the record is shared into. */
  shareCount: number;
  /** Whether the viewer may add shares; only changes the empty-state label. */
  canShare: boolean;
  /**
   * Set when the viewer only sees this record through a share. Not yet
   * derivable from the API — leave unset until the grant level is returned.
   */
  viewerLevel?: AccessLevel;
  /** Highlights the button while its panel is open. */
  isActive?: boolean;
  onClick: () => void;
}

/**
 * Badge-style header button that opens the record's Access panel. Sits beside
 * the page's other header actions, in the same place on every detail page.
 */
export const AccessButton = ({
  shareCount,
  canShare,
  viewerLevel,
  isActive = false,
  onClick,
}: Props) => {
  const { colors } = useTheme() as any;

  if (viewerLevel === "view") {
    return (
      <StyledAccessButton tone="muted" isActive={isActive} onClick={onClick}>
        <HiLockClosed size={14} color={colors.gray[80]} />
        View only
      </StyledAccessButton>
    );
  }

  if (viewerLevel === "edit") {
    return (
      <StyledAccessButton tone="blue" isActive={isActive} onClick={onClick}>
        <HiUsers size={16} color={colors.blue.primary} />
        Shared with you · Can edit
      </StyledAccessButton>
    );
  }

  return (
    <StyledAccessButton
      tone="default"
      isActive={isActive}
      onClick={onClick}
      title="Who can see and work on this record"
    >
      <HiUsers size={16} color={colors.gray[60]} />
      {shareCount > 0 ? "Shared" : canShare ? "Share" : "Not shared"}
      {shareCount > 0 && <CountPill>{shareCount}</CountPill>}
    </StyledAccessButton>
  );
};
